import { deleteHttp, post, put } from "../../../common/api/apiShared";
import { LegalEntitySelectorViewModel } from "../../../legalEntity/LegalEntity.types";
import { DataProcessor, ProcessingActivityModel, SelectableAgreement, SelectableLegalUnitsItem } from "../../ProcessingActivity.types";
import { ValidationError } from "../../../common/validationError";
import { useTranslation } from "../../../localization/useTranslation";
import { useSelectableLegalEntityQuery } from "../../../legalEntity/useSelectableLegalEntityQuery";
import { useSelectableDataCategoriesQuery } from "../../hooks/useSelectableDataCategoriesQuery";
import { useSelectableDocumentsForLegalEntitiesQuery, useSelectableDocumentsQuery } from "../../hooks/useSelectableDocumentQuery";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { UserSelectableItem } from "../../../user/User.types";
import {
    isTransferBasisMissingForDataProcessorSharing,
    isTransferImpactAssessmentMissingForDataProcessorSharing,
} from "../../../common/dataProcessorValidation";
import { UserPermissions } from "../../../auth/userContextProvider/UserContextProvider.types";
import { useTransferBasesQuery } from "../../../common/hooks/useSelectableItemQueries";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useState } from "react";
import { AgreementSaveModel, DataProcessorAgreementSaveModel } from "../agreementDialog/DataProcessorAgreementDialog.types";
import { useExpandedId } from "../../../common/hooks/useExpandedId";
import useCountryHook from "../../../common/hooks/useCountryList";
import { useOptimisticUpdate } from "../../../common/hooks/useOptimisticUpdate";
import { ContractOnTransfer, SelectableContract, toSelectableAgreement } from "../../../contract/contract/Contract.types.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { ClassificationInfo, DataProcessorClassicationViewModel } from "./DataProcessorStep.types.ts";

export function useDataProcessorsStepMapping(
    processingActivity: ProcessingActivityModel,
    groupEntityIsDataProcessor: boolean,
    onCreateNewDocument: (transferImpactAssessmentId: string, dataProcessorId: string) => void
) {
    const queryClient = useDotLegalQueryClient();
    const optimisticUpdate = useOptimisticUpdate();
    const { getExpandedId, setExpandedId } = useExpandedId();
    const { permissions } = useUserContext();
    const { addonsFeatures } = usePlanContext();
    const userPermissions = getUserPermissions();
    const [dataProcessorAgreementSaveModel, setDataProcessorAgreementSaveModel] = useState<DataProcessorAgreementSaveModel | undefined>(undefined);
    const [dataprocessorId, setDataprocessorId] = useState("");
    const [showSpecificStorageLocationDialog, setShowSpecificStorageLocationDialog] = useState(false);
    const { selectableCountries, countriesIsLoading, thirdCountries } = useCountryHook();

    let queryKey = "processingActivity" + processingActivity.id;

    let dataCategoriesQuery = useSelectableDataCategoriesQuery();
    let legalEntitiesQuery = useSelectableLegalEntityQuery();
    let responsibleQuery = useResponsibleQuery(userPermissions.hasResponsiblePermission);
    let sendersQuery = useDotLegalQueryWithKey<Array<LegalEntitySelectorViewModel>>(
        "senders" + groupEntityIsDataProcessor,
        "/LegalEntity/senders/" + groupEntityIsDataProcessor
    );
    let transferBasisQuery = useTransferBasesQuery(userPermissions.hasTiaAndTransferBasisPermission);

    const classificationsUrl = `/ProcessingActivity/${processingActivity.id}/DataProcessor/classifications`;
    const classificationsQuery = useDotLegalQuery<Array<DataProcessorClassicationViewModel>>(classificationsUrl, {
        enabled: addonsFeatures.research && !groupEntityIsDataProcessor,
    });

    const dataProcessorOnPa = processingActivity.dataProcessors.find((x) => x.id === getExpandedId());
    const legalEntityIdOnTransfer = dataProcessorOnPa?.dataProcessor ?? undefined;

    const contractsOnDataProcessorUrl = `/ProcessingActivity/DataProcessor/${getExpandedId()}/contracts`;
    const contractsOnDataProcessorQuery = useDotLegalQuery<ContractOnTransfer>(contractsOnDataProcessorUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer && !dataProcessorOnPa?.isSystemGenerated,
    });

    const contractsOnSystemSupplierUrl = `/system/supplier/${getExpandedId()}/contracts`;
    const contractsOnSystemSupplierQuery = useDotLegalQuery<ContractOnTransfer>(contractsOnSystemSupplierUrl, {
        enabled: !!legalEntityIdOnTransfer && dataProcessorOnPa?.isSystemGenerated,
    });

    const selectableContractsUrl = `/LegalEntity/${legalEntityIdOnTransfer}/selectableContracts`;
    const selectableContractsQuery = useDotLegalQuery<Array<SelectableContract>>(selectableContractsUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer,
    });

    let documentsQuery = useSelectableDocumentsQuery(
        userPermissions.hasTiaAndTransferBasisPermission || userPermissions.hasAgreementPermission,
        legalEntityIdOnTransfer,
        processingActivity.id
    );
    let subProcessorsDocumentsQuery = useSelectableDocumentsForLegalEntitiesQuery(
        userPermissions.hasTiaAndTransferBasisPermission || userPermissions.hasAgreementPermission,
        dataProcessorOnPa?.subProcessors?.filter((s) => s.processorId).map((s) => s.processorId!) ?? [],
        processingActivity.id
    );

    let dataCategoryLoading = dataCategoriesQuery.isLoading;
    let legalEntityLoading = legalEntitiesQuery.isLoading;
    let sendersLoading = sendersQuery.isLoading;
    let responsibleLoading = responsibleQuery.isLoading;
    let transferBasisLoading = transferBasisQuery.isLoading;
    let documentsLoading = documentsQuery.isLoading;

    let legalEntityData = legalEntitiesQuery.data;
    let sendersData = sendersQuery.data;
    let transferBasisData = transferBasisQuery.data;
    let dataProcessorAgreementDocs: Array<SelectableAgreement> = documentsQuery.dataProcessorDocuments.map((d) => {
        return {
            id: d.id,
            name: d.name,
            status: d.status,
            contractOrDocumentType: d.documentTypeName,
            type: "document",
        };
    });
    let transferImpactAssessmentDocs = documentsQuery.transferImpactAssessmentDocuments;

    let subProcessorsTIADocsLoaing = subProcessorsDocumentsQuery.isLoading;
    let subProcessorsTIADocs = subProcessorsDocumentsQuery.transferImpactAssessmentDocuments;

    const dataCategoriesData = dataCategoriesQuery.getDataCategoriesForProcessingActivity(processingActivity);

    const removeAgreementFromDataProcessorMutation = useDotLegalMutation(removeAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });
    const addAgreementToDataProcessorMutation = useDotLegalMutation(addAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });

    const onAgreementAdded = async (agreement: SelectableAgreement, dataProcessorId: string) => {
        if (agreement.type === "document") {
            const documentId = agreement.id;

            const newProcessingActivityModel = { ...processingActivity };
            const dataProcessor = newProcessingActivityModel.dataProcessors.find((d) => d.id === dataProcessorId)!;

            dataProcessor.dataProcessAgreements.push(documentId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementToDataProcessorMutation.mutateAsync, {
                processingActivityId: processingActivity.id,
                dataProcessorId: dataProcessorId,
                saveModel: { agreementId: documentId },
            });
        } else if (agreement.type === "contract") {
            const contractId = agreement.id;
            const contractsOnTransfer = contractsOnDataProcessorQuery.data!;
            contractsOnTransfer.contractIds.push(contractId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnDataProcessorUrl, addContractApi, {
                dataProcessorId: getExpandedId(),
                contractId: contractId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onAgreementDeleted = async (agreement: SelectableAgreement, dataProcessorId: string) => {
        if (agreement.type === "document") {
            const agreementId = agreement.id;
            const newProcessingActivityModel = { ...processingActivity };
            const dataProcessor = newProcessingActivityModel.dataProcessors.find((d) => d.id === dataProcessorId)!;

            dataProcessor.dataProcessAgreements = dataProcessor.dataProcessAgreements.filter((x) => x !== agreementId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, removeAgreementFromDataProcessorMutation.mutateAsync, {
                processingActivityId: processingActivity.id,
                dataProcessorId: dataProcessorId,
                saveModel: { agreementId: agreementId },
            });
        } else if (agreement.type === "contract") {
            const contractId = agreement.id;
            const contractsOnTransfer = contractsOnDataProcessorQuery.data!;
            contractsOnTransfer.contractIds = contractsOnTransfer.contractIds.filter((x) => x !== contractId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnDataProcessorUrl, removeContractApi, {
                dataProcessorId: getExpandedId(),
                contractId: contractId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onHasAgreementChange = async (hasAgreement: boolean, dataProcessorId: string) => {
        const newProcessingActivityModel = { ...processingActivity };
        const dataProcessor = newProcessingActivityModel.dataProcessors.find((d) => d.id === dataProcessorId)!;

        dataProcessor.hasDataProcessorAgreement = hasAgreement;
        dataProcessor.dataProcessAgreements = [];

        await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementToDataProcessorMutation.mutateAsync, {
            processingActivityId: processingActivity.id,
            dataProcessorId: dataProcessorId,
            saveModel: { hasAgreement: hasAgreement },
        });
    };

    const selectableContracts: Array<SelectableAgreement> = selectableContractsQuery.data?.map((c) => toSelectableAgreement(c)) ?? [];

    const onLegalEntityOnTransferChange = () => {
        if (contractsOnDataProcessorQuery.data) {
            const newData = { ...contractsOnDataProcessorQuery.data };
            newData.contractIds = [];
            queryClient.setQueryData(contractsOnDataProcessorUrl, newData);
        }
    };

    function getClassificationInfo(legalEntityId: string | null | undefined): ClassificationInfo | undefined {
        if (!legalEntityId || !addonsFeatures.research || groupEntityIsDataProcessor) {
            return undefined;
        }

        const rows = classificationsQuery.data?.filter((r) => r.legalEntityId === legalEntityId) ?? [];

        if (rows.length === 0) {
            return { hasClassification: false };
        }

        const latestRow = rows.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0];

        return {
            hasClassification: true,
            riskLevel: latestRow.riskLevel,
            responseId: latestRow.questionnaireResponseId,
            isSubmitted: !!latestRow.isSubmitted,
            riskName: latestRow.riskName,
            scoreEnabled: latestRow.scoreEnabled,
            questionnaireName: latestRow.questionnaireName,
        };
    }

    return {
        dataCategoryLoading,
        dataCategoriesData,
        legalEntityLoading,
        legalEntityData,
        responsibleLoading,
        sendersLoading,
        sendersData,
        transferBasisesLoading: transferBasisLoading,
        transferBasisesData: transferBasisData,
        agreementsLoading:
            documentsLoading || contractsOnDataProcessorQuery.isLoading || selectableContractsQuery.isLoading || contractsOnSystemSupplierQuery.isLoading,
        selectableAgreements: [...dataProcessorAgreementDocs, ...selectableContracts].sort((a, b) => a.name.localeCompare(b.name)),
        transferImpactAssessmentDocs,
        getResponsibles: responsibleQuery.data,
        dataProcessorAgreementSaveModel,
        setDataProcessorAgreementSaveModel,
        dataprocessorId,
        setDataprocessorId,
        saveAgreement,
        refetchDocuments: documentsQuery.refetch,
        getExpandedId,
        setExpandedId,
        subProcessorsTIADocsLoaing,
        subProcessorsTIADocs,
        showSpecificStorageLocationDialog,
        setShowSpecificStorageLocationDialog,
        selectableCountries,
        countriesIsLoading,
        thirdCountries,
        onAgreementAdded,
        onAgreementDeleted,
        onHasAgreementChange,
        selectedAgreements: [
            ...(processingActivity.dataProcessors.find((d) => d.id === getExpandedId())?.dataProcessAgreements ?? []),
            ...(contractsOnDataProcessorQuery.data?.contractIds ?? []),
            ...(contractsOnSystemSupplierQuery.data?.contractIds ?? []),
        ],
        containsNonAccessibleContacts:
            (contractsOnDataProcessorQuery.data?.containsNonAccessibleContacts || contractsOnSystemSupplierQuery.data?.containsNonAccessibleContacts) ?? false,
        onLegalEntityOnTransferChange,
        getClassificationInfo,
    };

    function getUserPermissions() {
        if (groupEntityIsDataProcessor) {
            return {
                hasAgreementPermission: permissions.processingActivityDataControllersPermissions.hasAgreementPermission,
                hasTiaAndTransferBasisPermission: permissions.processingActivityDataControllersPermissions.hasTiaAndTransferBasisPermission,
                hasResponsiblePermission: permissions.processingActivityDataControllersPermissions.hasResponsiblePermission,
            };
        } else {
            return {
                hasAgreementPermission: permissions.processingActivityDataProcessorsPermissions.hasAgreementPermission,
                hasTiaAndTransferBasisPermission: permissions.processingActivityDataProcessorsPermissions.hasTiaAndTransferBasisPermission,
                hasResponsiblePermission: permissions.processingActivityDataProcessorsPermissions.hasResponsiblePermission,
            };
        }
    }

    function saveAgreement(agreement: string, dataprocessor: string) {
        onCreateNewDocument(agreement, dataprocessor);
        documentsQuery.refetch();
    }

    async function addAgreementAPI(data: { processingActivityId: string; dataProcessorId: string; saveModel: AgreementSaveModel }) {
        return await put("/processingactivity/" + data.processingActivityId + "/dataprocessor/" + data.dataProcessorId + "/agreement", data.saveModel);
    }

    async function removeAgreementAPI(data: { processingActivityId: string; dataProcessorId: string; saveModel: AgreementSaveModel }) {
        return await deleteHttp(
            `/processingactivity/${data.processingActivityId}/dataprocessor/${data.dataProcessorId}/agreement?agreementId=${data.saveModel.agreementId}`
        );
    }

    async function addContractApi(data: { dataProcessorId: string; contractId: string }) {
        return await post(`/processingActivity/DataProcessor/${data.dataProcessorId}/contracts/${data.contractId}`, null);
    }

    async function removeContractApi(data: { dataProcessorId: string; contractId: string }) {
        return await deleteHttp(`/processingActivity/DataProcessor/${data.dataProcessorId}/contracts/${data.contractId}`);
    }
}

export function useValidateDataProcessorsStep(userPermissions: UserPermissions, canRegisterSelfAsDataProcessor: boolean) {
    const { translateString } = useTranslation();

    return (
        model: ProcessingActivityModel,
        legalEntities: Array<SelectableLegalUnitsItem> | undefined,
        thirdCountries: Array<string>,
        groupEntityIsDataProcessor: boolean,
        responsibles: Array<UserSelectableItem> | undefined
    ): Array<ValidationError> => {
        const result: Array<ValidationError> = [];

        if (groupEntityIsDataProcessor && !canRegisterSelfAsDataProcessor) {
            return result;
        }

        const permissions = getStepPermissions(groupEntityIsDataProcessor, userPermissions);

        if (permissions.hasSharingPermission) {
            const dataProcessors = model.dataProcessors;

            if (groupEntityIsDataProcessor && !model.disableDataControllers && !model.hasDataControllers) {
                result.push(new ValidationError("noDataSharingsDataProcessorGroupEntityisDataProcessor", translateString("atLeastOneSharingRequired")));
            }

            if (!groupEntityIsDataProcessor && !model.disableDataProcessors && !model.hasDataProcessors) {
                result.push(new ValidationError("noDataSharingsDataProcessor", translateString("atLeastOneSharingRequired")));
            }

            filterDataProcessors(groupEntityIsDataProcessor, dataProcessors).forEach((dataProcessor, index) => {
                if (permissions.hasSubProcessorsPermission) {
                    dataProcessor.subProcessors
                        .filter((x) => !x.isLink)
                        .forEach((subprocessor, subprocessorIndex) => {
                            let recipient = legalEntities?.find((x) => x.id === subprocessor.processorId);

                            if (!subprocessor.processorId) {
                                result.push(
                                    new ValidationError(`${dataProcessor.id},${subprocessorIndex}dataProcessorId`, translateString("dataProcessorRequired"))
                                );
                            }
                            if (subprocessor.processorId === dataProcessor.dataProcessor && dataProcessor.dataProcessor) {
                                result.push(
                                    new ValidationError(
                                        `${dataProcessor.id},${subprocessorIndex}dataProcessorId`,
                                        dataProcessor.isSystemGenerated
                                            ? translateString("subprocessorUsedAsProcessorChangeOnSystem")
                                            : translateString("subprocessorUsedAsProcessor")
                                    )
                                );
                            }
                            if (
                                dataProcessor.subProcessors.filter((entry) => entry.processorId === subprocessor.processorId).length > 1 &&
                                subprocessor.processorId
                            ) {
                                result.push(
                                    new ValidationError(
                                        `${dataProcessor.id},${subprocessorIndex}dataProcessorId`,
                                        dataProcessor.isSystemGenerated
                                            ? translateString("duplicateSubprocessorChangeOnSystem")
                                            : translateString("duplicateSubprocessor")
                                    )
                                );
                            }
                            if (
                                isTransferBasisMissingForDataProcessorSharing(
                                    recipient,
                                    subprocessor.transferBasisId,
                                    permissions.hasTiaAndTransferBasisPermission,
                                    thirdCountries,
                                    subprocessor.dataStorageLocations
                                )
                            ) {
                                result.push(
                                    new ValidationError(
                                        `${dataProcessor.id},${subprocessorIndex}transferBasisId`,
                                        dataProcessor.isSystemGenerated
                                            ? translateString("transferBasisRequiredChangeOnSystem")
                                            : translateString("transferBasisRequired")
                                    )
                                );
                            }
                            if (permissions.hasDataCategoriesPermissions && subprocessor.dataCategories.length === 0) {
                                result.push(
                                    new ValidationError(`${dataProcessor.id},${subprocessorIndex}dataCategories`, translateString("dataCategoriesRequired"))
                                );
                            }

                            if (dataProcessor.legalEntities.some((l) => l === subprocessor.processorId)) {
                                result.push(
                                    new ValidationError(
                                        `${dataProcessor.id},${subprocessorIndex}dataProcessorId`,
                                        dataProcessor.isSystemGenerated
                                            ? translateString("dataProcessorCantBeInCompaniesChangeOnSystem")
                                            : translateString("dataProcessorCantBeInCompanies")
                                    )
                                );
                            }

                            if (
                                isTransferImpactAssessmentMissingForDataProcessorSharing(
                                    recipient,
                                    subprocessor.hasTransferImpactAssessment,
                                    subprocessor.transferImpactAssessmentId,
                                    permissions.hasTiaAndTransferBasisPermission,
                                    subprocessor.transferBasisId,
                                    thirdCountries,
                                    subprocessor.dataStorageLocations,
                                    subprocessor.containsNotAccessibleTia
                                )
                            ) {
                                result.push(
                                    new ValidationError(
                                        `${dataProcessor.id},${subprocessorIndex}transferImpactAssessment`,
                                        dataProcessor.isSystemGenerated ? translateString("tiaRequiredChangeOnSystem") : translateString("tiaRequired")
                                    )
                                );
                            }
                        });
                }

                let recipient = legalEntities?.find((x) => x.id === dataProcessor.dataProcessor);

                if (!dataProcessor.dataProcessor) {
                    result.push(new ValidationError(`${dataProcessor.id}dataProcessorId`, translateString("dataProcessorRequired")));
                }
                if (dataProcessor.legalEntities.length === 0) {
                    result.push(new ValidationError(`${dataProcessor.id}legalEntities`, translateString("companiesRequired")));
                }

                if (!groupEntityIsDataProcessor) {
                    if (
                        isTransferImpactAssessmentMissingForDataProcessorSharing(
                            recipient,
                            dataProcessor.hasTransferImpactAssessment,
                            dataProcessor.transferImpactAssessmentId,
                            permissions.hasTiaAndTransferBasisPermission,
                            dataProcessor.transferBasisId,
                            thirdCountries,
                            dataProcessor.dataStorageLocations,
                            dataProcessor.containsNotAccessibleTia
                        )
                    ) {
                        result.push(
                            new ValidationError(
                                `${dataProcessor.id}transferImpactAssessment`,
                                dataProcessor.isSystemGenerated ? translateString("tiaRequiredChangeOnSystem") : translateString("tiaRequired")
                            )
                        );
                    }

                    if (
                        isTransferBasisMissingForDataProcessorSharing(
                            recipient,
                            dataProcessor.transferBasisId,
                            permissions.hasTiaAndTransferBasisPermission,
                            thirdCountries,
                            dataProcessor.dataStorageLocations
                        )
                    ) {
                        result.push(
                            new ValidationError(
                                `${dataProcessor.id}transferBasisId`,
                                dataProcessor.isSystemGenerated
                                    ? translateString("transferBasisRequiredChangeOnSystem")
                                    : translateString("transferBasisRequired")
                            )
                        );
                    }
                }

                if (permissions.hasDataCategoriesPermissions && dataProcessor.dataCategories.length === 0) {
                    result.push(new ValidationError(`${dataProcessor.id}dataCategories`, translateString("dataCategoriesRequired")));
                }
                if (dataProcessor.legalEntities.some((l) => l === dataProcessor.dataProcessor)) {
                    result.push(
                        new ValidationError(
                            `${dataProcessor.id}dataProcessorId`,
                            dataProcessor.isSystemGenerated
                                ? translateString("dataProcessorCantBeInCompaniesChangeOnSystem")
                                : translateString("dataProcessorCantBeInCompanies")
                        )
                    );
                }

                let user = responsibles?.find((x) => x.id === dataProcessor.responsible);
                if (user && !user.active) {
                    result.push(new ValidationError(`${dataProcessor.id}inactiveUser`, translateString("userDeactivated")));
                }
            });
        }
        return result;
    };
}

export function getStepPermissions(groupEntityIsDataProcessor: boolean, userPermissions: UserPermissions) {
    return {
        hasTiaAndTransferBasisPermission: groupEntityIsDataProcessor
            ? userPermissions.processingActivityDataControllersPermissions?.hasTiaAndTransferBasisPermission
            : userPermissions.processingActivityDataProcessorsPermissions?.hasTiaAndTransferBasisPermission,
        hasSharingPermission: groupEntityIsDataProcessor
            ? userPermissions.processingActivityDataControllersPermissions !== undefined
            : userPermissions.processingActivityDataProcessorsPermissions !== undefined,
        hasSubProcessorsPermission: groupEntityIsDataProcessor
            ? userPermissions.processingActivityDataControllersPermissions?.hasSubProcessorPermission
            : userPermissions.processingActivityDataProcessorsPermissions?.hasSubProcessorPermission,
        hasResponsiblePermission: groupEntityIsDataProcessor
            ? userPermissions.processingActivityDataControllersPermissions?.hasResponsiblePermission
            : userPermissions.processingActivityDataProcessorsPermissions?.hasResponsiblePermission,
        hasAgreementPermission: groupEntityIsDataProcessor
            ? userPermissions.processingActivityDataControllersPermissions?.hasAgreementPermission
            : userPermissions.processingActivityDataProcessorsPermissions?.hasAgreementPermission,
        hasDataCategoriesPermissions: groupEntityIsDataProcessor
            ? userPermissions.processingActivityDataControllersPermissions?.hasDataCategoriesPermission
            : userPermissions.processingActivityDataProcessorsPermissions?.hasDataCategoriesPermission,
        hasNameAccess: groupEntityIsDataProcessor
            ? userPermissions.processingActivityDataControllersPermissions?.hasNameAccess
            : userPermissions.processingActivityDataProcessorsPermissions?.hasNameAccess,
    };
}

export function filterDataProcessors(groupEntityIsDataProcessor: boolean, dataProcessors: Array<DataProcessor>) {
    if (groupEntityIsDataProcessor) return dataProcessors.filter((x) => x.hasDataControllerAccess);
    else return dataProcessors.filter((x) => x.hasDataProcessorAccess);
}

export function isDataProcessorReadOnly(dataProcessor: DataProcessor, isGroupEntityDataProcessor: boolean, isValidated: boolean) {
    return (
        dataProcessor.readOnly ||
        (isGroupEntityDataProcessor && !dataProcessor.createdAsGroupEntityIsDataProcessor) ||
        (!isGroupEntityDataProcessor && dataProcessor.createdAsGroupEntityIsDataProcessor) ||
        isValidated
    );
}

export function canAddNewSubProcessor(dataProcessor: DataProcessor, isGroupEntityDataProcessor: boolean, isValidated: boolean) {
    return (
        !dataProcessor.isSystemGenerated &&
        ((isGroupEntityDataProcessor && dataProcessor.createdAsGroupEntityIsDataProcessor) ||
            (!isGroupEntityDataProcessor && !dataProcessor.createdAsGroupEntityIsDataProcessor) ||
            isValidated)
    );
}
